/**
 * The platform seam.
 *
 * Everything else in this package is platform-agnostic: the same problem+json
 * handling, the same idempotency rules, the same 401 recovery and the same
 * endpoint contracts run on the web and on a phone. What differs between them
 * is exactly four things, and they are injected here rather than imported:
 *
 *   · where the API lives
 *   · where a refresh token may be kept (a browser has localStorage; a phone
 *     has an OS keychain, which is meaningfully better and must be used)
 *   · where a short-lived idempotency key may be kept
 *   · where a random UUID comes from — a browser has `crypto.randomUUID`;
 *     React Native's Hermes runtime has NO `crypto` global at all
 *
 * Injecting them is what stops the mobile client forking the business logic.
 * A second copy of "when may a 409 be retried" is a second answer to that
 * question, and the two would drift.
 */
/** A store that fails closed: used before configuration, and in tests. */
const nullStore = {
    get: () => null,
    set: () => undefined,
    remove: () => undefined,
};
/**
 * The web's UUID source, and the default.
 *
 * A platform without `crypto.randomUUID` must inject its own. Failing loudly
 * here is deliberate: minting a weak key from `Math.random` would silently
 * weaken the one guarantee `Idempotency-Key` exists to provide.
 */
const globalRandomUUID = () => {
    const platformCrypto = globalThis.crypto;
    if (platformCrypto?.randomUUID === undefined) {
        throw new Error('No crypto.randomUUID on this platform. Inject one through configureApi({ randomUUID }).');
    }
    return platformCrypto.randomUUID();
};
let runtime = {
    baseUrl: '/api',
    secureStore: nullStore,
    sessionStore: nullStore,
    randomUUID: globalRandomUUID,
};
/** Called once at application start, before any request is made. */
export function configureApi(next) {
    runtime = { ...runtime, ...next };
}
export function apiRuntime() {
    return runtime;
}
/**
 * Wraps an asynchronous secure store as a synchronous one by keeping a copy in
 * memory. The token store is read on every request and cannot await; the phone's
 * keychain is asynchronous. So the value is loaded once at start-up and written
 * through on change — the keychain remains the durable copy, and the memory copy
 * never outlives the process.
 */
export function memoryBackedStore(async_, onError) {
    const cache = new Map();
    return {
        get: (key) => cache.get(key) ?? null,
        set: (key, value) => {
            cache.set(key, value);
            void async_.set(key, value).catch((error) => onError?.(error));
        },
        remove: (key) => {
            cache.delete(key);
            void async_.remove(key).catch((error) => onError?.(error));
        },
        async hydrate(keys) {
            for (const key of keys) {
                try {
                    const value = await async_.get(key);
                    if (value !== null)
                        cache.set(key, value);
                }
                catch (error) {
                    onError?.(error);
                }
            }
        },
    };
}
//# sourceMappingURL=runtime.js.map